import Link from 'next/link'
import { Hit } from 'react-instantsearch-core'
import styled from 'styled-components'

import { t } from 'localization'
import { headerNavigationTrackEvent } from 'mParticle/headerNavigationTrackEvent'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import getConfig from 'next/config'
import { useCallback, useEffect, useRef, useState } from 'react'
import { H6 } from 'shared/text'
import { colors, mediaQueries } from 'shared/theme'
import ExpandedMenuModal, { MenuPanel } from '../components/ExpandedMenuModal'
import { MenuLink, UnderlinedMenuLink } from '../components/MenuLink'
import SearchItem from './SearchItem'

const TP = 'layout.navigation.search.searchMenu'

export type SearchHit = {
  grid_picture_url: string
  lowest_price_cents: number
  name: string
  slug: string
  url: string
}

type SearchMenuProps = {
  hits: Hit<SearchHit>[]
  isOpen: boolean
  onRequestClose: () => void
  searchText: string
}

type SearchResults = {
  name: string
  slug: string
  grid_picture_url: string
}

type SuggestionProduct = {
  category: string
  id: string
  imageUrl: string
  slug: string
  title: string
}

type SuggestionCollection = {
  recordId: string
  recordSubtype: string
  recordType: number
  slug: string
  title: string
}

type ConsumerSuggestionResults = {
  data: {
    products: SuggestionProduct[]
    collections: SuggestionCollection[]
  }
}

function fromSuggestionProductListToSearchResultList(
  results: ConsumerSuggestionResults,
): SearchResults[] {
  return results.data.products?.map((item) => ({
    name: item.title,
    slug: item.slug,
    grid_picture_url: item.imageUrl,
  }))
}

export const SearchMenu = ({ isOpen, onRequestClose, searchText }: SearchMenuProps) => {
  const [searchResults, setSearchResults] = useState<SearchResults[]>()
  const madeInitialCall = useRef<boolean>(false)

  const { publicRuntimeConfig } = getConfig()

  const getSearchSuggestions = useCallback(async (term: string) => {
    const query = {
      term: (term as string) || '',
      salesChannel: '2', // FC
      productLimit: '10',
      pageOffset: '1',
    }

    const url = '/api/consumer-search?' + new URLSearchParams(query).toString()
    const results = await fetch(url, {
      method: 'GET',
    }).then((results) => results.json())

    setSearchResults(fromSuggestionProductListToSearchResultList(results))

    if (term) {
      sendTrackingEvent('SEARCH_BAR_TYPE_PAUSE', {
        page: window?.location?.href,
        search_string: term,
        service_name: 'consumer_search',
      })
    }
  }, [])

  useEffect(() => {
    if (!isOpen && (searchText || '').length === 0) {
      setSearchResults([])
    }
  }, [isOpen, searchText])

  useEffect(() => {
    if (isOpen && !madeInitialCall.current) {
      madeInitialCall.current = true
      void getSearchSuggestions('')
    }
  }, [madeInitialCall, isOpen, getSearchSuggestions])

  useEffect(() => {
    if (isOpen) {
      void getSearchSuggestions(searchText)
    }
  }, [searchText, isOpen, getSearchSuggestions])

  const categoryLinks = [
    {
      href: '/air-jordans',
      label: 'Air Jordan',
    },
    {
      href: '/nike',
      label: 'Nike',
    },
    {
      href: '/adidas/adidas-yeezy',
      label: 'Yeezy',
    },
    {
      href: '/adidas',
      label: 'Adidas',
    },
    {
      href: '/off-white',
      label: 'Off-White',
    },
    {
      href: '/sneakers',
      label: t(`${TP}.moreBrands`, 'More Brands'),
    },
    {
      href: '/new-releases',
      label: t(`${TP}.newReleases`, 'New Releases'),
    },
    {
      href: '/price-drops',
      label: t(`${TP}.priceDrops`, 'Price Drops'),
    },
    {
      href: '/men',
      label: t(`${TP}.men`, 'Men'),
    },
    {
      href: '/women',
      label: t(`${TP}.women`, 'Women'),
    },
    {
      href: '/youth',
      label: t(`${TP}.kids`, 'Kids'),
    },
    {
      href: '/apparel',
      label: t(`${TP}.apparel`, 'Apparel'),
    },
  ]

  const data = searchResults || []
  const getSearchMenuTitle = () => {
    if (searchText?.length === 0) {
      return t(`${TP}.popular`, 'Popular')
    }
    if (data?.length === 0) {
      return t(`${TP}.noResults`, 'No Results')
    }
    return t(`${TP}.topResults`, 'Top Results')
  }

  return (
    <ExpandedMenuModal
      contentLabel={t(`${TP}.searchMenu`, 'Search Menu')}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      qaAttr="NavigationSearchMenu"
    >
      <MenusContainer>
        <SearchResultsPanel className="scrollable-menu">
          <Title data-qa="NavigationSearchMenuTitle">{getSearchMenuTitle()}</Title>
          <ItemWrapper>
            {data &&
              data.map(({ name, slug, grid_picture_url }, index) => (
                <StyledSearchItem
                  href={`/${slug}`}
                  imageUrl={
                    grid_picture_url || publicRuntimeConfig.IMAGE_FC_CDN + 'missing.png?w=360'
                  }
                  key={slug}
                  index={index}
                  title={name}
                  qaAttr={`navigation-search-item-${index}`}
                  searchText={searchText}
                />
              ))}
          </ItemWrapper>
        </SearchResultsPanel>
        {searchText?.length > 0 && data && data?.length > 0 && (
          <AllResultsContainer>
            <Link href={`/catalogsearch/result?query=${searchText}`} passHref legacyBehavior>
              <UnderlinedMenuLink onClick={(e) => headerNavigationTrackEvent(e)}>
                {t(`${TP}.viewAllResults`, 'View All Results')}
              </UnderlinedMenuLink>
            </Link>
          </AllResultsContainer>
        )}
        {searchText?.length === 0 && (
          <CategoryPanel>
            <Title>{t(`${TP}.categories`, 'Categories')}</Title>
            <CategoriesContainer>
              {categoryLinks.map((link, index) => (
                <Link href={link.href} key={link.href} passHref legacyBehavior>
                  <StyledMenuLink
                    onClick={() =>
                      sendTrackingEvent('SEARCH_BAR_CATEGORY_TAP', {
                        page: window?.location?.href,
                        slug: link.href,
                        slug_type: 'product_list',
                        category_name: link.label,
                      })
                    }
                    data-qa={`navigation-category-link-${index}`}
                  >
                    {link.label}
                  </StyledMenuLink>
                </Link>
              ))}
            </CategoriesContainer>
          </CategoryPanel>
        )}
      </MenusContainer>
    </ExpandedMenuModal>
  )
}

const StyledMenuLink = styled(MenuLink)`
  margin-bottom: 16px !important;
`

const AllResultsContainer = styled.div`
  padding: 30px;
  padding-left: 32px;
  flex: 1;
  ${mediaQueries.smallNavigation} {
    padding-bottom: 100px;
  }
  ${mediaQueries.tabletOrLarger} {
    padding-top: 85px;
  }
`

const MenusContainer = styled.div`
  display: flex;
  ${mediaQueries.smallNavigation} {
    max-height: none;
    flex-direction: column;
  }
`

const SearchResultsPanel = styled(MenuPanel)`
  flex: 1;
  ${mediaQueries.smallNavigation} {
    max-height: none;
  }
  ${mediaQueries.notNavigation} {
    overflow: hidden auto;
    -webkit-overflow-scrolling: touch;
  }
`

export const ItemWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`

export const StyledSearchItem = styled(SearchItem)`
  flex: 1;
  flex-basis: 50%;
  min-width: 280px;
  flex-flow: row wrap;
  padding: 15px 15px 0;

  &:focus-visible {
    outline: 5px auto Highlight;
    outline: 5px auto -webkit-focus-ring-color;
  }
`

const CategoryPanel = styled(MenuPanel)`
  flex: 1;
  ${mediaQueries.mobile} {
    display: none;
  }
`

const Title = styled(H6)`
  color: ${colors.darkGray};
`

const CategoriesContainer = styled.div`
  columns: 2;
  margin-top: 25px;
`

export default SearchMenu
